export type BeatLineSongLike = {
  id?: string | number;
  title?: string;
  year?: string | number;
};

export type BeatLineCardOptions = {
  locked?: boolean;
  lockedTitle?: string;
  lockedArtist?: string;
};

export type BeatLineCardHtmlContext = {
  state?: {
    qrMode?: boolean;
    mode?: string;
    playerCount?: number;
  } | Record<string, unknown>;
  online?: {
    enabled?: boolean;
  } | Record<string, unknown>;
  beatlineSettings?: {
    scanUrl?: string;
  } | Record<string, unknown>;
  t?: (key: string, fallback?: string) => string;
  escapeHtml?: (value: unknown) => string;
  findYouTubeIdForCard?: (song: BeatLineSongLike) => string | number | null | undefined;
};

function fallbackEscapeHtml(value: unknown): string {
  const div = document.createElement('div');
  div.textContent = String(value || '');
  return div.innerHTML;
}

function readBool(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

function readNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function translate(ctx: BeatLineCardHtmlContext, key: string, fallback: string): string {
  if (typeof ctx.t === 'function') return ctx.t(key, fallback);
  return fallback;
}

/**
 * C4 extracted module: exact card DOM contract from the original BEATLINE createCardHTML().
 *
 * This function intentionally returns the same class names, data attributes and child order
 * as the plugin. The legacy runtime delegates to this module, so this is now the first
 * real game rendering function owned by TypeScript.
 */
export function createCardHTML(
  song: BeatLineSongLike | null | undefined,
  revealed: boolean,
  opts: BeatLineCardOptions = {},
  ctx: BeatLineCardHtmlContext = {}
): string {
  const escapeHtml = ctx.escapeHtml || fallbackEscapeHtml;
  const fullTitle = song && song.title ? String(song.title) : '';
  let artist = '???';
  let title = fullTitle;

  if (fullTitle && fullTitle.includes(' - ')) {
    const parts = fullTitle.split(' - ');
    artist = parts[0];
    title = parts.slice(1).join(' - ');
  }

  const isLocked = !!(opts && opts.locked);

  let cssClass = 'card';
  if (revealed) cssClass += ' revealed';
  if (isLocked) cssClass += ' locked-card';

  const year = song ? song.year : undefined;
  const hasYear = typeof year !== 'undefined';
  const yearText = revealed && hasYear ? String(year) : '';
  const yearAttr = revealed && hasYear ? ` data-year="${escapeHtml(year)}"` : '';

  let displayTitle = translate(ctx, 'listen_placeholder', 'Luister...');
  let displayArtist = 'BeatLine';

  if (revealed) {
    displayTitle = title;
    displayArtist = artist;
  } else if (isLocked) {
    displayTitle = opts && typeof opts.lockedTitle === 'string' && opts.lockedTitle
      ? opts.lockedTitle
      : translate(ctx, 'opponent_turn', 'Beurt van tegenstander');
    displayArtist = opts && typeof opts.lockedArtist === 'string' && opts.lockedArtist
      ? opts.lockedArtist
      : translate(ctx, 'please_wait', 'EVEN GEDULD...');
  }

  let qrHtml = '';
  try {
    const state = ctx.state || {};
    const online = ctx.online || {};
    const canQr = !!(
      !revealed &&
      !isLocked &&
      state &&
      readBool((state as Record<string, unknown>).qrMode) &&
      String((state as Record<string, unknown>).mode || '') === 'spotify' &&
      readNumber((state as Record<string, unknown>).playerCount, 0) >= 2 &&
      readNumber((state as Record<string, unknown>).playerCount, 0) <= 4 &&
      (!online || !readBool((online as Record<string, unknown>).enabled))
    );

    if (canQr && song && song.id) {
      const tid = encodeURIComponent(String(song.id).trim());
      const settings = ctx.beatlineSettings || {};
      const base = settings && typeof (settings as Record<string, unknown>).scanUrl === 'string'
        ? String((settings as Record<string, unknown>).scanUrl)
        : '/scanner/';
      let scanUrl = base + (base.indexOf('?') === -1 ? '?' : '&') + 'tid=' + tid;
      try {
        const y = ctx.findYouTubeIdForCard ? ctx.findYouTubeIdForCard(song) : '';
        if (y) scanUrl += '&yid=' + encodeURIComponent(String(y));
      } catch (error) {
        // Match original plugin behavior: QR fallback silently ignores YouTube lookup failures.
      }
      const qrImg = 'https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=' + encodeURIComponent(scanUrl);
      qrHtml = `<div class="card-qr-wrap"><img class="card-qr" src="${qrImg}" alt="QR" loading="lazy"></div>`;
    }
  } catch (error) {
    // Match original plugin behavior: QR failures must never break card rendering.
  }

  const iconOrQr = qrHtml && qrHtml.length ? qrHtml : '<div class="card-icon">🎵</div>';

  const safeArtist = escapeHtml(displayArtist);
  const safeTitle = escapeHtml(displayTitle);
  const fullArtistAttr = revealed ? ` data-full-text="${safeArtist}" tabindex="0"` : '';
  const fullTitleAttr = revealed ? ` data-full-text="${safeTitle}" tabindex="0"` : '';
  const id = song && song.id ? escapeHtml(song.id) : '';

  return `<div class="${cssClass}" data-id="${id}"${yearAttr}>
            <div class="card-artist"${fullArtistAttr}>${safeArtist}</div>
            ${iconOrQr}
            <div class="card-year">${escapeHtml(yearText)}</div>
            <div class="card-title"${fullTitleAttr}>${safeTitle}</div>
        </div>`;
}
